import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { fetchRecipesFromShopify, searchRecipesByTerm } from '../redux/actions/recipeActions';
import BlogCard from '../components/blogs/BlogCard';
import { Recipe } from '../types/shopify';
import GrocerizeLoadingWheel from '../components/grocerize/GrocerizeLoadingWheel';
import PageTemplate from './PageTemplate';
import BlogBanner from '../components/blogs/BlogBanner';
import { fetchAllBlogs } from '../clients/shopify/shopifyClient';

interface BlogsProps extends RouteComponentProps {
  fetchRecipesFromShopify: (blogHandle?: string, cursor?: string) => Promise<Recipe[]>;
  searchRecipesByTerm: (searchTerm: string, blogHandle?: string) => Promise<Recipe[]>;
  shopifyRecipes: Recipe[];
  isLoading: boolean;
  error: string | null;
  paginationInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
    searchTerm?: string | null;
  };
}

const Blogs: React.FC<BlogsProps> = ({
  fetchRecipesFromShopify,
  searchRecipesByTerm,
  shopifyRecipes,
  isLoading,
  error,
  paginationInfo,
  location
}) => {
  const [blogHandle, setBlogHandle] = useState('recipes');
  const [blogCategories, setBlogCategories] = useState<{id: string, handle: string, title: string}[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const blogsPerPage = 9; // 3x3 grid
  
  // Parse the search query from URL
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search') || '';
  
  // Fetch all available blog categories when component mounts
  useEffect(() => {
    const loadBlogCategories = async () => {
      try {
        setCategoriesLoading(true);
        const blogs = await fetchAllBlogs();
        if (blogs && blogs.length > 0) {
          console.log('Fetched blogs:', blogs);
          setBlogCategories(blogs);
        } else {
          // Fallback to static blog categories if API returns none
          console.log('No blogs found or error fetching blogs. Using static fallback options.');
          setBlogCategories([
            { id: 'static-recipes', handle: 'recipes', title: 'Recipes' },
            { id: 'static-news', handle: 'news', title: 'News' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching blog categories:', error);
        // Fallback to static blog categories on error
        setBlogCategories([
          { id: 'static-recipes', handle: 'recipes', title: 'Recipes' },
          { id: 'static-news', handle: 'news', title: 'News' }
        ]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    
    loadBlogCategories();
  }, []);
  
  // Handle blog category change
  const handleBlogCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newBlogHandle = event.target.value;
    setBlogHandle(newBlogHandle);
    setCurrentPage(1); // Reset to first page when changing category
  };
  
  useEffect(() => {
    // Fetch blog posts when component mounts, search changes, or blog handle changes
    if (searchQuery) {
      searchRecipesByTerm(searchQuery, blogHandle);
    } else {
      fetchRecipesFromShopify(blogHandle);
    }
  }, [fetchRecipesFromShopify, searchRecipesByTerm, blogHandle, searchQuery]);
  
  // Calculate total pages
  const totalPages = Math.ceil(shopifyRecipes.length / blogsPerPage);
  
  // Get current blogs
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = shopifyRecipes.slice(indexOfFirstBlog, indexOfLastBlog);
  
  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  
  const loadMoreBlogs = () => {
    if (paginationInfo?.hasNextPage && paginationInfo?.endCursor) {
      fetchRecipesFromShopify(blogHandle, paginationInfo.endCursor);
    }
  };
  
  return (
    <PageTemplate sidebarType="none" headerType="magenta" hideItemSearching={true}>
      {/* Banner at the top of the page - full width */}
      {!searchQuery && (
        <BlogBanner 
          title="Grocerize Blog" 
          subtitle="Tips, news and insights to help you save on your grocery shopping" 
        />
      )}
      <div className="u-background--lightgrey">
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div className={`${!searchQuery ? 'u-pt--24' : 'u-pt--32'} u-pb--16`}>
            <h1 className="u-main-font--xlarge u-bold">{searchQuery ? `Search Results: ${searchQuery}` : 'Latest Blog Posts'}</h1>
            <p className="u-main-font--medium">
              {searchQuery 
                ? `Found ${shopifyRecipes.length} results for "${searchQuery}"` 
                : 'Browse our collection of blogs and articles'}
            </p>
            
            {/* Blog category selector - prominent button style */}
            <div style={{ marginTop: '25px', marginBottom: '25px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '16px' }}>
                Blog Categories:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {/* Always show at least the default "recipes" category */}
                <button
                  onClick={() => {
                    setBlogHandle('recipes');
                    // Store blog handle in sessionStorage for BlogDetail to use
                    sessionStorage.setItem('current_blog_handle', 'recipes');
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '20px',
                    border: 'none',
                    backgroundColor: blogHandle === 'recipes' ? '#9300DA' : '#f0f0f0',
                    color: blogHandle === 'recipes' ? 'white' : '#333',
                    cursor: 'pointer',
                    fontWeight: blogHandle === 'recipes' ? 'bold' : 'normal',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Recipes
                </button>
                
                {/* Add a "News" category button */}
                <button
                  onClick={() => {
                    setBlogHandle('news');
                    // Store blog handle in sessionStorage for BlogDetail to use
                    sessionStorage.setItem('current_blog_handle', 'news');
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '20px',
                    border: 'none',
                    backgroundColor: blogHandle === 'news' ? '#9300DA' : '#f0f0f0',
                    color: blogHandle === 'news' ? 'white' : '#333',
                    cursor: 'pointer',
                    fontWeight: blogHandle === 'news' ? 'bold' : 'normal',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  News
                </button>
                
                {/* Dynamically loaded categories (beyond the default ones) */}
                {blogCategories
                  .filter(category => !['recipes', 'news'].includes(category.handle)) // Exclude already shown static categories
                  .map(category => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setBlogHandle(category.handle);
                        // Store blog handle in sessionStorage for BlogDetail to use
                        sessionStorage.setItem('current_blog_handle', category.handle);
                      }}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '20px',
                        border: 'none',
                        backgroundColor: blogHandle === category.handle ? '#9300DA' : '#f0f0f0',
                        color: blogHandle === category.handle ? 'white' : '#333',
                        cursor: 'pointer',
                        fontWeight: blogHandle === category.handle ? 'bold' : 'normal',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {category.title}
                    </button>
                  ))
                }
                
                {categoriesLoading && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    color: '#666',
                    padding: '10px 20px',
                  }}>
                    Loading categories...
                  </div>
                )}
              </div>
            </div>
            {searchQuery && (
              <button 
                onClick={() => {
                  // Clear search and go back to all blogs
                  window.location.href = '/browse/blogs';
                }}
                style={{
                  background: '#9300DA',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  marginTop: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#7b00b8';
                  e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.15)';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.background = '#7b00b8';
                  e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#9300DA';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.background = '#9300DA';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Clear Search
              </button>
            )}
          </div>
          
          {error && (
            <div className="u-background--red u-color-white u-p--16 u-br--small u-mb--16">
              <p className="u-main-font--medium">{error}</p>
            </div>
          )}
          
          {isLoading && shopifyRecipes.length === 0 ? (
            <div className="u-flex u-justify-center u-p--32">
              <GrocerizeLoadingWheel />
            </div>
          ) : (
            <>
              {/* Blog grid with responsive columns */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: '30px',
                justifyContent: 'center',
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '20px 0 40px'
              }}>
                {currentBlogs.map(blog => (
                  <BlogCard 
                    key={blog.id} 
                    shopifyBlog={{
                      ...blog,
                      // Ensure blogHandle is set for each article
                      blogHandle: blog.blogHandle || blogHandle
                    }} 
                    isShopifyBlog={true}
                    currentBlogHandle={blogHandle}
                  />
                ))}
              </div>
              
              {/* Pagination */}
              {shopifyRecipes.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  marginBottom: '40px',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: '1px solid #ddd',
                        background: currentPage === number ? '#9300DA' : 'white',
                        color: currentPage === number ? 'white' : '#333',
                        cursor: 'pointer',
                        fontWeight: currentPage === number ? 'bold' : 'normal',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {number}
                    </button>
                  ))}
                  {paginationInfo?.hasNextPage && (
                    <button
                      onClick={loadMoreBlogs}
                      disabled={isLoading}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: '1px solid #ddd',
                        background: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      &gt;
                    </button>
                  )}
                </div>
              )}
              
              {shopifyRecipes.length === 0 && !isLoading && (
                <div className="u-p--32 u-text-center">
                  <p className="u-main-font--large">No blog posts found</p>
                  <p className="u-main-font--medium">Check back later for new content</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageTemplate>
  );
};

const mapStateToProps = (state: any) => ({
  shopifyRecipes: state.recipes.shopifyRecipes || [],
  isLoading: state.recipes.isLoading,
  error: state.recipes.error,
  paginationInfo: state.recipes.paginationInfo
});

const mapDispatchToProps = {
  fetchRecipesFromShopify,
  searchRecipesByTerm
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Blogs));
